import type { RecipeFamily } from "./types";

interface AssignmentResult {
  assignment: Record<string, string>;
  canonicalMatchCount: number;
  matchedIngredientCount: number;
}

function assignmentKey(assignment: Record<string, string>): string {
  return Object.entries(assignment)
    .sort(([slotA], [slotB]) => slotA.localeCompare(slotB))
    .map(([slotId, ingredientId]) => `${slotId}:${ingredientId}`)
    .join("|");
}

export function matchSelectedIngredients(
  recipe: RecipeFamily,
  selectedIngredientIds: string[],
): AssignmentResult | null {
  if (selectedIngredientIds.length === 0) {
    return {
      assignment: {},
      canonicalMatchCount: 0,
      matchedIngredientCount: 0,
    };
  }

  const choices = selectedIngredientIds
    .map((ingredientId) => ({
      ingredientId,
      slots: recipe.slots
        .filter((slot) =>
          slot.options.some((option) => option.ingredientId === ingredientId),
        )
        .map((slot) => slot.id)
        .sort(),
    }))
    .filter((choice) => choice.slots.length > 0)
    .sort(
      (a, b) =>
        a.slots.length - b.slots.length ||
        a.ingredientId.localeCompare(b.ingredientId),
    );

  if (choices.length === 0) return null;

  const slotById = new Map(recipe.slots.map((slot) => [slot.id, slot]));
  let best: AssignmentResult | null = null;

  function visit(
    index: number,
    usedSlots: Set<string>,
    assignment: Record<string, string>,
    canonicalCount: number,
    matchedCount: number,
  ) {
    if (index === choices.length) {
      const candidate = {
        assignment: { ...assignment },
        canonicalMatchCount: canonicalCount,
        matchedIngredientCount: matchedCount,
      };
      if (
        best === null ||
        candidate.matchedIngredientCount > best.matchedIngredientCount ||
        (candidate.matchedIngredientCount === best.matchedIngredientCount &&
          (candidate.canonicalMatchCount > best.canonicalMatchCount ||
            (candidate.canonicalMatchCount === best.canonicalMatchCount &&
              assignmentKey(candidate.assignment) <
                assignmentKey(best.assignment))))
      ) {
        best = candidate;
      }
      return;
    }

    const choice = choices[index];
    for (const slotId of choice.slots) {
      if (usedSlots.has(slotId)) continue;
      const recipeSlot = slotById.get(slotId);
      if (!recipeSlot) continue;

      usedSlots.add(slotId);
      assignment[slotId] = choice.ingredientId;
      visit(
        index + 1,
        usedSlots,
        assignment,
        canonicalCount +
          (recipeSlot.canonicalIngredientId === choice.ingredientId ? 1 : 0),
        matchedCount + 1,
      );
      delete assignment[slotId];
      usedSlots.delete(slotId);
    }

    // Skip this choice: OR semantics allow partial ingredient matching
    visit(index + 1, usedSlots, assignment, canonicalCount, matchedCount);
  }

  visit(0, new Set(), {}, 0, 0);
  return best;
}
